import { useDisclosure } from '@chakra-ui/hooks';
import React from 'react';
import TableRowActions from '../../shared/TableRowActions';
import AddInsuranceModal from './modal/AddInsuranceModal';
import { InsurancePolicy } from '../../../../../types/AdvisoryReport';
import { useDeleteInsurancePolicy } from '../../../api/current-insurances/deleteInsurance';
import useAdvisoryReportCreationParams from '../../../hooks/useCreationFlowSubject';
import { RemoveAlertDialog } from '../../../../../../../design-system/src';
import { useTranslation } from 'react-i18next';
import { Option } from '../../../../../../../design-system/src/content/components/SearchPickerInput';
import { useDeletePackageInsurancePolicy } from '../../../api/current-insurances/deletePackageInsurance';
import { PartyType } from '../../../../../../../../types/SMT';

interface Props {
  insurancePolicy: InsurancePolicy;
  customRiskObjects?: Option[];
  partyType: PartyType;
  controlled?: boolean;
  hideAIBanner?: boolean;
  // Actions
  onEdit?: (insurancePolicy: InsurancePolicy) => void;
  onRemove?: (id: string, packageId?: string) => void;
}

const InsurancePolicyTableRowActions: React.FC<Props> = ({
  controlled,
  insurancePolicy,
  customRiskObjects,
  partyType,
  onEdit,
  onRemove,
  hideAIBanner,
}) => {
  const { t } = useTranslation();
  const editModal = useDisclosure();
  const { reportId } = useAdvisoryReportCreationParams();
  const removePolicyModal = useDisclosure();
  const { mutate: deleteInsurancePolicy } = useDeleteInsurancePolicy();
  const { mutate: deletePackageInsurancePolicy } =
    useDeletePackageInsurancePolicy();
  const policyType =
    insurancePolicy.type === 'ACTIVE' ? 'CURRENT-INSURANCE' : 'OFFER';

  const handleRemove = () => {
    if (controlled)
      return onRemove?.(insurancePolicy.id, insurancePolicy.policy_package_id);
    if (insurancePolicy.policy_package_id) {
      deletePackageInsurancePolicy({
        insuranceId: insurancePolicy.id,
        reportId,
        packageId: insurancePolicy.policy_package_id,
      });
    } else {
      deleteInsurancePolicy({
        insuranceId: insurancePolicy.id,
        reportId,
        type: policyType,
      });
    }
  };

  return (
    <>
      <AddInsuranceModal
        key={JSON.stringify(insurancePolicy)}
        mode="EDIT"
        initialData={insurancePolicy}
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        type={policyType}
        partyType={partyType}
        customRiskObjects={customRiskObjects}
        controlled={controlled}
        onSubmit={onEdit}
        hideAIBanner={hideAIBanner}
      />
      <RemoveAlertDialog
        isOpen={removePolicyModal.isOpen}
        onClose={removePolicyModal.onClose}
        title={t('ARE-YOU-SURE')}
        description={t(
          'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_INSURANCE_REMOVE-ALERT',
          { name: insurancePolicy.name },
        )}
        onConfirm={handleRemove}
      />
      <TableRowActions
        onEdit={editModal.onOpen}
        onRemove={removePolicyModal.onOpen}
        eventName={policyType?.toLowerCase().replace('-', ' ')}
      />
    </>
  );
};

export default InsurancePolicyTableRowActions;
